import {
  conOpciones,
  type GuardarEncuesta,
  NOMBRE_TIPO_DE_PREGUNTA,
  type Pregunta,
  problemasDeEncuesta,
  TIPOS_DE_PREGUNTA,
  type TipoDePregunta,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { Modal } from "../componentes/ModalFirma.tsx";
import { Aviso, Boton, Campo, CLASE_ENTRADA, EstadoVacio, Tarjeta } from "../componentes/ui.tsx";
import { mensajeDe } from "../utiles.ts";

type Formulario = Omit<GuardarEncuesta, "id">;

const VACIA: Formulario = {
  titulo: "",
  descripcion: "",
  anonima: false,
  paraTodos: true,
  destinatarios: [],
  cierraEn: null,
  preguntas: [],
};

/** `crypto.randomUUID` no existe en http, y así se prueba desde la tablet en la red local. */
const idNuevo = () => `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const preguntaNueva = (tipo: TipoDePregunta = "unica"): Pregunta => ({
  id: idNuevo(),
  tipo,
  texto: "",
  obligatoria: true,
  opciones: conOpciones(tipo) ? ["", ""] : [],
});

/** Honduras es UTC−6 todo el año: la encuesta cierra al terminar ese día. */
const diaDeCierre = (valor: string) => (valor ? new Date(`${valor}T23:59:59-06:00`) : null);
const valorDeCierre = (fecha: Date | null) =>
  fecha ? new Date(fecha.getTime() - 6 * 3_600_000).toISOString().slice(0, 10) : "";

export function PaginaNuevaEncuesta() {
  return <Editor inicial={VACIA} />;
}

/** Un borrador ya guardado: se carga y sigue el mismo formulario. */
export function PaginaEditarEncuesta() {
  const { id } = useParams({ from: "/app/encuestas/$id/editar" });
  const trpc = useTRPC();
  const borrador = useQuery(trpc.encuestas.borrador.queryOptions({ id }));
  if (borrador.error) return <EstadoVacio>{mensajeDe(borrador.error)}</EstadoVacio>;
  if (!borrador.data) return <EstadoVacio>Cargando…</EstadoVacio>;
  const { id: _, ...inicial } = borrador.data;
  return <Editor key={id} id={id} inicial={inicial} />;
}

function Editor({ id, inicial }: { id?: string; inicial: Formulario }) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const [f, setF] = useState(inicial);
  const [error, setError] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const cambiar = (parte: Partial<Formulario>) => setF((v) => ({ ...v, ...parte }));

  const guardar = useMutation(trpc.encuestas.guardar.mutationOptions());
  const publicar = useMutation(trpc.encuestas.publicar.mutationOptions());
  const descartar = useMutation(trpc.encuestas.descartar.mutationOptions());
  const ocupado = guardar.isPending || publicar.isPending || descartar.isPending;

  const alListado = () => {
    queryClient.invalidateQueries();
    navigate({ to: "/encuestas" });
  };

  async function alGuardar() {
    setError(null);
    try {
      await guardar.mutateAsync({ ...f, id });
      alListado();
    } catch (e) {
      setError(mensajeDe(e));
    }
  }

  async function alPublicar() {
    setError(null);
    try {
      const guardada = await guardar.mutateAsync({ ...f, id });
      await publicar.mutateAsync({ id: guardada.id });
      alListado();
    } catch (e) {
      setConfirmando(false);
      setError(mensajeDe(e));
    }
  }

  function revisarYConfirmar() {
    const problemas = [
      ...(f.titulo.trim().length < 3 ? ["Ponle un título a la encuesta"] : []),
      ...problemasDeEncuesta(f),
    ];
    if (problemas.length > 0) return setError(problemas.join(". "));
    setError(null);
    setConfirmando(true);
  }

  const cambiarPregunta = (i: number, parte: Partial<Pregunta>) =>
    cambiar({ preguntas: f.preguntas.map((p, j) => (j === i ? { ...p, ...parte } : p)) });
  function moverPregunta(i: number, delta: number) {
    const preguntas = [...f.preguntas];
    const [movida] = preguntas.splice(i, 1);
    if (movida) preguntas.splice(i + delta, 0, movida);
    cambiar({ preguntas });
  }

  return (
    <div className="mx-auto max-w-[760px]">
      <Encabezado overline="Encuestas" titulo={id ? "Editar encuesta" : "Nueva encuesta"} />
      <div className="space-y-4">
        <Tarjeta titulo="Datos">
          <div className="space-y-3">
            <Campo etiqueta="Título">
              <input
                value={f.titulo}
                onChange={(e) => cambiar({ titulo: e.target.value })}
                placeholder="Encuesta de clima laboral 2026"
                className={CLASE_ENTRADA}
              />
            </Campo>
            <Campo etiqueta="Descripción" ayuda="Para qué es y qué se hará con los resultados.">
              <textarea
                value={f.descripcion}
                onChange={(e) => cambiar({ descripcion: e.target.value })}
                rows={2}
                className={CLASE_ENTRADA}
              />
            </Campo>
            <Campo
              etiqueta="Fecha de cierre"
              ayuda="Opcional. Sin fecha, queda abierta hasta que la cierres."
            >
              <input
                type="date"
                value={valorDeCierre(f.cierraEn)}
                onChange={(e) => cambiar({ cierraEn: diaDeCierre(e.target.value) })}
                className={`${CLASE_ENTRADA} max-w-[200px]`}
              />
            </Campo>
          </div>
        </Tarjeta>

        <Tarjeta titulo="Anonimato">
          <Opcion
            activa={!f.anonima}
            onClick={() => cambiar({ anonima: false })}
            titulo="Con nombre"
            detalle="Verás qué respondió cada persona."
          />
          <Opcion
            activa={f.anonima}
            onClick={() => cambiar({ anonima: true })}
            titulo="Anónima"
            detalle="Nadie podrá ver qué respondió cada quien, ni los administradores. Solo se sabe quién ya contestó, y los resultados se ven al cerrarla."
          />
        </Tarjeta>

        <Tarjeta titulo="A quién va">
          <Opcion
            activa={f.paraTodos}
            onClick={() => cambiar({ paraTodos: true })}
            titulo="Toda la empresa"
            detalle="Todo el personal activo al momento de publicar."
          />
          <Opcion
            activa={!f.paraTodos}
            onClick={() => cambiar({ paraTodos: false })}
            titulo="Elegir personas"
            detalle="Por proceso o una por una."
          />
          {!f.paraTodos && (
            <Personas
              elegidas={f.destinatarios}
              onCambio={(destinatarios) => cambiar({ destinatarios })}
            />
          )}
        </Tarjeta>

        {f.preguntas.map((p, i) => (
          <TarjetaDePregunta
            key={p.id}
            numero={i + 1}
            total={f.preguntas.length}
            pregunta={p}
            onCambio={(parte) => cambiarPregunta(i, parte)}
            onMover={(delta) => moverPregunta(i, delta)}
            onQuitar={() => cambiar({ preguntas: f.preguntas.filter((_, j) => j !== i) })}
          />
        ))}
        <Boton
          variante="secundario"
          className="w-full"
          onClick={() => cambiar({ preguntas: [...f.preguntas, preguntaNueva()] })}
        >
          Agregar pregunta
        </Boton>

        {error && <Aviso>{error}</Aviso>}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
          {id && (
            <Boton
              variante="peligro"
              className="mr-auto"
              disabled={ocupado}
              onClick={async () => {
                await descartar.mutateAsync({ id });
                alListado();
              }}
            >
              Descartar borrador
            </Boton>
          )}
          <Boton variante="secundario" disabled={ocupado} onClick={() => void alGuardar()}>
            Guardar borrador
          </Boton>
          <Boton variante="firma" disabled={ocupado} onClick={revisarYConfirmar}>
            Publicar
          </Boton>
        </div>
      </div>

      {confirmando && (
        <Modal titulo="Publicar la encuesta" onCerrar={() => setConfirmando(false)}>
          <div className="space-y-4">
            <p className="text-[13px] text-texto-2">
              Se le avisará a{" "}
              {f.paraTodos ? "todo el personal activo" : `${f.destinatarios.length} personas`}.
              Después de publicarla ya no se podrán cambiar las preguntas, ni si es anónima.
            </p>
            <div className="flex justify-end gap-2">
              <Boton variante="discreto" onClick={() => setConfirmando(false)}>
                Cancelar
              </Boton>
              <Boton variante="firma" disabled={ocupado} onClick={() => void alPublicar()}>
                Publicar
              </Boton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Opcion({
  activa,
  onClick,
  titulo,
  detalle,
}: {
  activa: boolean;
  onClick: () => void;
  titulo: string;
  detalle: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={activa}
      onClick={onClick}
      className={`mb-2 block w-full rounded-md border px-3 py-2 text-left text-[13px] ${
        activa
          ? "border-marino bg-marino-claro font-semibold text-marino"
          : "border-borde bg-white text-texto-2 hover:bg-fondo"
      }`}
    >
      {titulo}
      <span className="block text-[11.5px] font-normal text-texto-3">{detalle}</span>
    </button>
  );
}

function Personas({
  elegidas,
  onCambio,
}: {
  elegidas: string[];
  onCambio: (ids: string[]) => void;
}) {
  const trpc = useTRPC();
  const personas = useQuery(trpc.encuestas.personas.queryOptions());
  const [filtro, setFiltro] = useState("");
  const lista = personas.data ?? [];
  const procesos = useMemo(() => [...new Set(lista.flatMap((p) => p.procesos))].sort(), [lista]);
  const visibles = lista.filter((p) =>
    `${p.nombre} ${p.puesto}`.toLowerCase().includes(filtro.trim().toLowerCase()),
  );
  const alternar = (id: string) =>
    onCambio(elegidas.includes(id) ? elegidas.filter((e) => e !== id) : [...elegidas, id]);
  const sumarProceso = (codigo: string) => {
    const delProceso = lista.filter((p) => p.procesos.includes(codigo)).map((p) => p.id);
    onCambio([...new Set([...elegidas, ...delProceso])]);
  };

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11.5px] font-semibold text-texto-2">Agregar un proceso:</span>
        {procesos.map((codigo) => (
          <Boton key={codigo} variante="secundario" compacto onClick={() => sumarProceso(codigo)}>
            {codigo}
          </Boton>
        ))}
      </div>
      <input
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
        placeholder="Buscar por nombre o puesto"
        aria-label="Buscar personas"
        className={CLASE_ENTRADA}
      />
      <ul className="max-h-[260px] divide-y divide-borde/60 overflow-y-auto rounded-md border border-borde">
        {visibles.map((p) => (
          <li key={p.id}>
            <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] hover:bg-fondo">
              <input
                type="checkbox"
                checked={elegidas.includes(p.id)}
                onChange={() => alternar(p.id)}
              />
              <span className="min-w-0 flex-1 truncate">
                {p.nombre}
                <span className="text-texto-3"> · {p.puesto}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <p className="text-[11.5px] text-texto-3">
        {elegidas.length === 1 ? "1 persona elegida" : `${elegidas.length} personas elegidas`}
      </p>
    </div>
  );
}

function TarjetaDePregunta({
  numero,
  total,
  pregunta: p,
  onCambio,
  onMover,
  onQuitar,
}: {
  numero: number;
  total: number;
  pregunta: Pregunta;
  onCambio: (parte: Partial<Pregunta>) => void;
  onMover: (delta: number) => void;
  onQuitar: () => void;
}) {
  function cambiarTipo(tipo: TipoDePregunta) {
    const opciones = !conOpciones(tipo)
      ? []
      : p.opciones.length > 0
        ? p.opciones
        : preguntaNueva(tipo).opciones;
    onCambio({ tipo, opciones });
  }
  const cambiarOpcion = (i: number, valor: string) =>
    onCambio({ opciones: p.opciones.map((o, j) => (j === i ? valor : o)) });

  return (
    <Tarjeta titulo={`Pregunta ${numero}`}>
      <div className="space-y-3">
        <Campo etiqueta="Pregunta">
          <input
            value={p.texto}
            onChange={(e) => onCambio({ texto: e.target.value })}
            className={CLASE_ENTRADA}
          />
        </Campo>
        <Campo etiqueta="Tipo de respuesta">
          <select
            value={p.tipo}
            onChange={(e) => cambiarTipo(e.target.value as TipoDePregunta)}
            className={CLASE_ENTRADA}
          >
            {TIPOS_DE_PREGUNTA.map((t) => (
              <option key={t} value={t}>
                {NOMBRE_TIPO_DE_PREGUNTA[t]}
              </option>
            ))}
          </select>
        </Campo>
        {conOpciones(p.tipo) && (
          <div className="space-y-1.5">
            {p.opciones.map((o, i) => (
              // Las opciones no tienen id propio: su lugar en la lista es lo que las identifica.
              // biome-ignore lint/suspicious/noArrayIndexKey: se editan en su sitio
              <div key={i} className="flex gap-2">
                <input
                  value={o}
                  onChange={(e) => cambiarOpcion(i, e.target.value)}
                  placeholder={`Opción ${i + 1}`}
                  aria-label={`Opción ${i + 1} de la pregunta ${numero}`}
                  className={CLASE_ENTRADA}
                />
                <Boton
                  variante="discreto"
                  aria-label={`Quitar la opción ${i + 1}`}
                  onClick={() => onCambio({ opciones: p.opciones.filter((_, j) => j !== i) })}
                >
                  Quitar
                </Boton>
              </div>
            ))}
            <Boton
              variante="secundario"
              compacto
              onClick={() => onCambio({ opciones: [...p.opciones, ""] })}
            >
              Agregar opción
            </Boton>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t border-borde pt-3">
          <label className="mr-auto flex items-center gap-2 text-[12.5px] text-texto-2">
            <input
              type="checkbox"
              checked={p.obligatoria}
              onChange={(e) => onCambio({ obligatoria: e.target.checked })}
            />
            Obligatoria
          </label>
          <Boton variante="discreto" disabled={numero === 1} onClick={() => onMover(-1)}>
            Subir
          </Boton>
          <Boton variante="discreto" disabled={numero === total} onClick={() => onMover(1)}>
            Bajar
          </Boton>
          <Boton variante="peligro" compacto onClick={onQuitar}>
            Quitar pregunta
          </Boton>
        </div>
      </div>
    </Tarjeta>
  );
}
